import React from 'react';
import OutcomeSection from './OutcomeSection';
import ComplaintSummarySection from './ComplaintSummarySection';
import WhatHappenedSection from './WhatHappenedSection';
import ReasoningSection from './ReasoningSection';
import RedressGuidanceSection from './RedressGuidanceSection';
import PrecedentsSection from './PrecedentsSection';
import { CaseWorkspaceState } from '../../../../../types/case-workspace';
import { Skeleton } from '../../../../ui/skeleton';
import ReportSectionHeading from './ReportSectionHeading';

interface OmbudsmanReportProps {
  reportSections: CaseWorkspaceState['reportSections'];
  streamingSection: CaseWorkspaceState['streamingSection'];
  isStreaming: boolean;
  expandedPrecedents: string[];
  onTogglePrecedent: (drn: string) => void;
  onExpandAllPrecedents: () => void;
  onDecisionClick?: (decisionIdOrReference: string) => void;
  signalVerbatim?: string;
}

const OmbudsmanReport: React.FC<OmbudsmanReportProps> = ({
  reportSections,
  streamingSection,
  isStreaming,
  expandedPrecedents,
  onTogglePrecedent,
  onExpandAllPrecedents,
  onDecisionClick,
  signalVerbatim,
}) => {
  const isUpheld = reportSections.outcome?.outcome === 'upheld' || reportSections.outcome?.outcome === 'partially_upheld';

  return (
    <div className="bg-white">
      {/* 1. Complaint Summary */}
      {reportSections.complaintSummary ? (
        <ComplaintSummarySection 
          summary={reportSections.complaintSummary} 
          verbatim={signalVerbatim}
          onDecisionClick={onDecisionClick} 
        />
      ) : streamingSection === 'summary' ? (
        <section className="border-b border-border px-6 py-5">
          <ReportSectionHeading number={1} title="Summary" className="mb-4" />
          <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
            <div className="animate-pulse">Generating summary...</div>
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-primary rounded-full animate-bounce" />
              <span className="w-2 h-2 bg-primary rounded-full animate-bounce delay-100" />
              <span className="w-2 h-2 bg-primary rounded-full animate-bounce delay-200" />
            </div>
          </div>
        </section>
      ) : isStreaming ? (
        <section className="border-b border-border px-6 py-5">
          <ReportSectionHeading number={1} title="Summary" className="mb-4" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-5/6" />
        </section>
      ) : null}

      {/* 2. What Happened */}
      {reportSections.whatHappened ? (
        <WhatHappenedSection events={reportSections.whatHappened} onDecisionClick={onDecisionClick} />
      ) : streamingSection === 'what_happened' ? (
        <section className="border-b border-border px-6 py-5">
          <ReportSectionHeading number={2} title="Timeline" className="mb-4" />
          <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
            <div className="animate-pulse">Analyzing timeline...</div>
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-primary rounded-full animate-bounce" />
              <span className="w-2 h-2 bg-primary rounded-full animate-bounce delay-100" />
              <span className="w-2 h-2 bg-primary rounded-full animate-bounce delay-200" />
            </div>
          </div>
        </section>
      ) : isStreaming ? (
        <section className="border-b border-border px-6 py-5">
          <ReportSectionHeading number={2} title="Timeline" className="mb-4" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-11/12" />
            <Skeleton className="h-4 w-10/12" />
          </div>
        </section>
      ) : null}

      {/* 3. Ombudsman's Reasoning */}
      {reportSections.reasoning ? (
        <ReasoningSection reasoning={reportSections.reasoning} onDecisionClick={onDecisionClick} />
      ) : streamingSection === 'reasoning' ? (
        <section className="border-b border-border px-6 py-5">
          <ReportSectionHeading number={3} title="Guidance" className="mb-4" />
          <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
            <div className="animate-pulse">Building guidance...</div>
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-primary rounded-full animate-bounce" />
              <span className="w-2 h-2 bg-primary rounded-full animate-bounce delay-100" />
              <span className="w-2 h-2 bg-primary rounded-full animate-bounce delay-200" />
            </div>
          </div>
        </section>
      ) : isStreaming ? (
        <section className="border-b border-border px-6 py-5">
          <ReportSectionHeading number={3} title="Guidance" className="mb-4" />
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
            <div className="pt-2 flex gap-2">
              <Skeleton className="h-6 w-24 rounded-full" />
              <Skeleton className="h-6 w-28 rounded-full" />
              <Skeleton className="h-6 w-32 rounded-full" />
            </div>
          </div>
        </section>
      ) : null}

      {/* 4. Redress Guidance (Indicative) — only for upheld/partially upheld */}
      {isUpheld && (<>
      {reportSections.redressGuidance ? (
        <RedressGuidanceSection guidance={reportSections.redressGuidance} onDecisionClick={onDecisionClick} />
      ) : streamingSection === 'redress' ? (
        <section className="border-b border-border px-6 py-5">
          <ReportSectionHeading number={4} title="Redress Guidance (Indicative)" className="mb-4" />
          <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
            <div className="animate-pulse">Calculating redress...</div>
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-primary rounded-full animate-bounce" />
              <span className="w-2 h-2 bg-primary rounded-full animate-bounce delay-100" />
              <span className="w-2 h-2 bg-primary rounded-full animate-bounce delay-200" />
            </div>
          </div>
        </section>
      ) : isStreaming ? (
        <section className="border-b border-border px-6 py-5">
          <ReportSectionHeading number={4} title="Redress Guidance (Indicative)" className="mb-4" />
          <div className="space-y-3">
            <Skeleton className="h-20 w-full rounded-lg" />
            <div className="grid grid-cols-2 gap-3">
              <Skeleton className="h-16 rounded-lg" />
              <Skeleton className="h-16 rounded-lg" />
            </div>
            <Skeleton className="h-4 w-3/4" />
          </div>
        </section>
      ) : null}
      </>)}

      {/* 5. Precedent Cases */}
      {reportSections.precedents ? (
        <PrecedentsSection
          precedents={reportSections.precedents}
          expandedPrecedents={expandedPrecedents}
          onTogglePrecedent={onTogglePrecedent}
          onExpandAllPrecedents={onExpandAllPrecedents}
          onDecisionClick={onDecisionClick}
          caseSummary={reportSections.complaintSummary?.summary || signalVerbatim}
        />
      ) : streamingSection === 'precedents' ? (
        <section className="border-b border-border px-6 py-5">
          <ReportSectionHeading number={5} title="Supporting precedent" className="mb-4" />
          <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
            <div className="animate-pulse">Finding precedent cases...</div>
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-primary rounded-full animate-bounce" />
              <span className="w-2 h-2 bg-primary rounded-full animate-bounce delay-100" />
              <span className="w-2 h-2 bg-primary rounded-full animate-bounce delay-200" />
            </div>
          </div>
        </section>
      ) : isStreaming ? (
        <section className="border-b border-border px-6 py-5">
          <ReportSectionHeading number={5} title="Supporting precedent" className="mb-4" />
          <div className="space-y-4">
            <div className="border border-border rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-start">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-6 w-16 rounded-full" />
              </div>
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
            </div>
            <div className="border border-border rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-start">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-6 w-16 rounded-full" />
              </div>
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          </div>
        </section>
      ) : null}

      {/* 6. Outcome Section (Guidance Only) */}
      {reportSections.outcome ? (
        <OutcomeSection outcome={reportSections.outcome} onDecisionClick={onDecisionClick} />
      ) : streamingSection === 'outcome' ? (
        <section className="border-t border-border bg-[var(--go-spring)] px-6 py-5">
          <div className="rounded-lg border border-[var(--go-warm-border)] bg-background p-4">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
              Potential outcome
            </p>
            <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
              <div className="animate-pulse">Analyzing outcome...</div>
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-primary rounded-full animate-bounce" />
                <span className="w-2 h-2 bg-primary rounded-full animate-bounce delay-100" />
                <span className="w-2 h-2 bg-primary rounded-full animate-bounce delay-200" />
              </div>
            </div>
          </div>
        </section>
      ) : isStreaming ? (
        <section className="border-t border-border bg-[var(--go-spring)] px-6 py-5">
          <div className="rounded-lg border border-[var(--go-warm-border)] bg-background p-4">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
              Potential outcome
            </p>
            <div className="mb-3 flex items-center gap-2">
              <Skeleton className="h-6 w-28 rounded-full" />
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default OmbudsmanReport;
